// Domain marks for the SeaState meteograms — the v1 Chart.js "plugins" made
// first-class. Each is a small pure draw function over the chart's ChartCtx.
// Colours resolve from CSS custom properties at draw time (light/dark both work).

import type { Mark } from "./chart.js";

export function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v.length > 0 ? v : fallback;
}

// Hex colour + alpha -> rgba(). Non-hex input is returned unchanged.
export function rgba(color: string, alpha: number): string {
  let h = color.trim();
  if (!h.startsWith("#")) return color;
  h = h.slice(1);
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

// Vertical line marking the current hour.
export function nowLine(index: number): Mark {
  return {
    layer: "front",
    draw({ ctx, xPx, area }) {
      if (index < 0) return;
      const color = cssVar("--now", "#d1495b");
      const x = Math.round(xPx(index)) + 0.5;
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(x, area.top);
      ctx.lineTo(x, area.bottom);
      ctx.stroke();
      ctx.font = "600 10px system-ui, sans-serif";
      ctx.fillStyle = color;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText("now", x + 3, area.top + 2);
      ctx.restore();
    },
  };
}

// Generic per-hour level shading behind the data: merge adjacent same-level
// hours into one rect and fill with tint(level); tint may return null to skip.
// (nightBands and the squall wash are both instances of this pattern.)
export function shadeBands(levels: number[], tint: (lvl: number) => string | null): Mark {
  return {
    layer: "back",
    draw({ ctx, xPx, area }) {
      const n = levels.length;
      if (n === 0) return;
      const half = n > 1 ? (xPx(1) - xPx(0)) / 2 : (area.right - area.left) / 2;
      ctx.save();
      let run: { start: number; lvl: number } | null = null;
      const flush = (end: number): void => {
        if (run === null) return;
        const fill = tint(run.lvl);
        if (fill !== null) {
          const x0 = xPx(run.start) - (run.start > 0 ? half : 0);
          const x1 = xPx(end - 1) + half;
          ctx.fillStyle = fill;
          ctx.fillRect(x0, area.top, x1 - x0, area.bottom - area.top);
        }
        run = null;
      };
      for (let i = 0; i <= n; i++) {
        const lvl = i < n ? levels[i] : 0;
        if (run !== null && lvl !== run.lvl) flush(i);
        if (lvl > 0 && run === null) run = { start: i, lvl };
      }
      ctx.restore();
    },
  };
}

/** Darkness washes by level (0 daylight · 1 civil · 2 nautical · 3 night).
 *  Exported because the conditions strip draws its light row from the same
 *  table — two shades for the same hour would read as two different skies. */
export const NIGHT_TINTS = [
  "",
  "rgba(38,48,86,0.06)",
  "rgba(30,40,78,0.11)",
  "rgba(22,31,64,0.16)",
] as const;

// Day/night backdrop: shade sunset->sunrise behind the data. `levels[i]` is the
// darkness level of hour i: 0 daylight, 1 civil twilight, 2 nautical, 3 night.
export function nightBands(levels: number[]): Mark {
  const dusk = NIGHT_TINTS;
  return {
    layer: "back",
    draw({ ctx, xPx, area }) {
      const n = levels.length;
      if (n === 0) return;
      const half = n > 1 ? (xPx(1) - xPx(0)) / 2 : (area.right - area.left) / 2;
      ctx.save();
      let run: { start: number; lvl: number } | null = null;
      const flush = (end: number): void => {
        if (run === null) return;
        const x0 = xPx(run.start) - (run.start > 0 ? half : 0);
        const x1 = xPx(end - 1) + half;
        ctx.fillStyle = dusk[run.lvl] ?? dusk[3];
        ctx.fillRect(x0, area.top, x1 - x0, area.bottom - area.top);
        run = null;
      };
      for (let i = 0; i <= n; i++) {
        const lvl = i < n ? levels[i] : 0;
        if (run !== null && lvl !== run.lvl) flush(i);
        if (lvl > 0 && run === null) run = { start: i, lvl };
      }
      ctx.restore();
    },
  };
}

// Horizontal operating-limit line + a faintly tinted "over limit" zone. Set
// `below: true` when low is bad (e.g. visibility) so the zone shades downward.
export function refLine(opts: {
  value: number;
  label?: string;
  color?: string;
  below?: boolean;
}): Mark {
  return {
    layer: "front",
    draw({ ctx, yPx, area }) {
      const color = opts.color ?? cssVar("--crit", "#c23b3b");
      const yp = yPx(opts.value);
      if (yp < area.top || yp > area.bottom) return;
      ctx.save();
      ctx.fillStyle = rgba(color, 0.06);
      if (opts.below === true) ctx.fillRect(area.left, yp, area.right - area.left, area.bottom - yp);
      else ctx.fillRect(area.left, area.top, area.right - area.left, yp - area.top);
      ctx.strokeStyle = rgba(color, 0.55);
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(area.left, yp);
      ctx.lineTo(area.right, yp);
      ctx.stroke();
      ctx.setLineDash([]);
      const txt = opts.label ?? "limit";
      ctx.font = "600 10px system-ui, sans-serif";
      ctx.textBaseline = "alphabetic";
      const tw = ctx.measureText(txt).width;
      ctx.fillStyle = cssVar("--surface", "#ffffff");
      ctx.fillRect(area.right - tw - 9, yp - 13, tw + 8, 13);
      ctx.fillStyle = color;
      ctx.textAlign = "left";
      ctx.fillText(txt, area.right - tw - 5, yp - 3);
      ctx.restore();
    },
  };
}

/**
 * The vessel's operating limit: a dashed horizontal rule at `value` in the
 * "at your limit" red (`--crit`). Unlike {@link refLine} this draws the line
 * alone — no tinted half-plane — so it can be paired with {@link noGoSpans},
 * which carries the over-limit signal in x instead of y.
 *
 * Layer: "front". The limit is the thing the whole chart is read against, so it
 * must stay legible on top of series fills and no-go shading; a limit line lost
 * under an area fill is worse than no limit line at all.
 *
 * @param value Limit in the chart's own y units (e.g. 25 kn, 2.5 m).
 * @param opts.label Right-aligned caption on the line; default `"limit"`.
 * @param opts.color Override the `--crit` red (rarely wanted).
 */
export function limitLine(
  value: number,
  opts?: { label?: string; color?: string },
): Mark {
  return {
    layer: "front",
    draw({ ctx, yPx, area }) {
      const color = opts?.color ?? cssVar("--crit", "#b23b3b");
      const yp = yPx(value);
      if (yp < area.top || yp > area.bottom) return; // limit off-scale: nothing honest to draw
      ctx.save();
      ctx.strokeStyle = color;
      ctx.setLineDash([5, 4]);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(area.left, Math.round(yp) + 0.5);
      ctx.lineTo(area.right, Math.round(yp) + 0.5);
      ctx.stroke();
      ctx.setLineDash([]);
      // Caption on a surface-coloured plate so it stays readable over gridlines.
      const txt = opts?.label ?? "limit";
      ctx.font = "600 10px system-ui, sans-serif";
      ctx.textBaseline = "alphabetic";
      const tw = ctx.measureText(txt).width;
      ctx.fillStyle = cssVar("--surface", "#ffffff");
      ctx.fillRect(area.right - tw - 9, yp - 13, tw + 8, 13);
      ctx.fillStyle = color;
      ctx.textAlign = "left";
      ctx.fillText(txt, area.right - tw - 5, yp - 3);
      ctx.restore();
    },
  };
}

/**
 * No-go shading: a translucent `--crit` band over every contiguous run of
 * samples that breaches the operating limit. The point of this mark is the
 * *gaps* — the unshaded stretches are the deployable weather windows.
 *
 * Layer: "back". The bands are a backdrop for reading the traces; drawn in
 * front they would wash out the very series the operator is judging.
 *
 * Nulls (data gaps) are NOT treated as under the limit — an unknown hour is not
 * a window you can plan a deployment into. By default (`unknownIsNoGo`) a null
 * counts as breaching, so a gap is shaded and never reads as a clear window.
 * Pass `unknownIsNoGo: false` when the series is legitimately sparse (e.g. buoy
 * observations) and shading every hole would be noise; runs then simply end at
 * the gap, and the gap is left bare rather than being claimed as safe.
 *
 * @param data The series values, aligned 1:1 with the chart's xLabels.
 * @param limit Threshold in the same units as `data`.
 * @param opts.below Low is bad (e.g. visibility): breach when value < limit.
 * @param opts.unknownIsNoGo Treat null/non-finite as a breach; default true.
 * @param opts.alpha Fill opacity; default 0.10.
 * @param opts.color Override the `--crit` red.
 */
export function noGoSpans(
  data: Array<number | null>,
  limit: number,
  opts?: { below?: boolean; unknownIsNoGo?: boolean; alpha?: number; color?: string },
): Mark {
  const below = opts?.below === true;
  const unknownIsNoGo = opts?.unknownIsNoGo ?? true;
  const breached = (v: number | null): boolean => {
    if (v === null || !Number.isFinite(v)) return unknownIsNoGo;
    return below ? v < limit : v > limit;
  };
  return {
    layer: "back",
    draw({ ctx, xPx, area }) {
      const n = data.length;
      if (n === 0) return;
      // Bands cover the sample's own hour cell, so a single breaching hour is
      // still a visible band rather than a zero-width line.
      const half = n > 1 ? (xPx(1) - xPx(0)) / 2 : (area.right - area.left) / 2;
      const fill = rgba(opts?.color ?? cssVar("--crit", "#b23b3b"), opts?.alpha ?? 0.1);
      ctx.save();
      ctx.fillStyle = fill;
      let start: number | null = null;
      const flush = (end: number): void => {
        if (start === null) return;
        const x0 = xPx(start) - (start > 0 ? half : 0);
        const x1 = xPx(end - 1) + (end < n ? half : 0);
        ctx.fillRect(x0, area.top, x1 - x0, area.bottom - area.top);
        start = null;
      };
      for (let i = 0; i <= n; i++) {
        const bad = i < n && breached(data[i]);
        if (!bad) flush(i);
        else if (start === null) start = i;
      }
      ctx.restore();
    },
  };
}

/**
 * The direction glyph — ONE shape for every quantity this console draws a
 * bearing for: wind, wave, swell and current alike. A filled head on a stroked
 * shaft, the ordinary arrow.
 *
 * There used to be a second, wave-specific shape here, and the record of why
 * there is not is worth more than the shape was. Dr Les Bender specified "wave
 * direction arrows, double arrowhead, no shaft"; that shipped, and then three
 * forms were tried in three days. A stroked double chevron collapsed near 45°
 * and 135° into a corner bracket, and collided with U.S. Chart No. 1 H44
 * (overfalls, tide rips, races). A filled double head collided with RYA/USCG
 * vector plotting, where the NUMBER of heads on a track is itself notation: one
 * water track, two ground track, three tidal set. A single filled head, shipped
 * and looked at on the map, read as nothing at all — an isolated shaftless
 * triangle at 14 px does not announce that it is pointing.
 *
 * The constraint that forced all three is gone. It was never really about the
 * sea needing its own mark: it was that the map's Workability layer drew wind
 * and wave INTERLEAVED in one space, and with colour already spent on the field
 * ramp, shape was the only channel left to tell them apart. The map now draws
 * one field at a time and says which — see map/windcontrol.ts — so nothing has
 * to be told apart by shape any more. On the charts and in the conditions table
 * the question never arose: wind and waves sit on separate labelled panels and
 * rows, exactly as Dr Bender's own MATLAB meteogram separates them, and the row
 * names the quantity better than any glyph could.
 *
 * So this is the dominant wave-direction mark across ~20 marine products, it is
 * what deepblueanalysis.com draws (a plain quiver arrow), and it collides with
 * nothing. It is still not a STANDARD: WMO-702 says only "arrows" and codifies
 * nothing further, and U.S. Chart No. 1 covers currents and tide rips but not
 * sea state. Common, not correct — and the help text says exactly that.
 */
export const ARROW_GLYPH = {
  /** Head length along the axis, CSS px — an absolute size, NOT a fraction of
   *  the shaft. The two surfaces that draw this differ only in shaft length
   *  (the chart ribbon's half-length is 7 px, the conditions table cell's 5.5),
   *  and scaling the head with the shaft would put a 2.7-px triangle in the
   *  table, which at devicePixelRatio 1 is three lit pixels and no point. The
   *  head is a legibility floor, so it holds its size and the shaft grows. */
  head: 3.4,
  /** Half-angle off the axis, degrees. 64° included at the apex: wide enough to
   *  read as a head rather than a spike at the end of a line, and far enough
   *  from the right angle that turned the old chevron into a bracket. */
  halfAngle: 32,
} as const;

/**
 * The house direction convention, in one place: **quote FROM, draw TOWARD.**
 *
 * Wind, waves and swell are quoted as the bearing they come FROM — that is what
 * the models publish (verified by measurement, not documentation: see
 * docs/GLYPH_FINDINGS.md), and it is how a mariner says it. Currents are quoted
 * as SET, the bearing they run TOWARD. Every marine product in the survey draws
 * the way the flow is GOING regardless of which way it quotes, and so do we.
 *
 * That leaves exactly one number that must be got right per quantity, and it
 * used to be written out twice — `dirArrows` here and a private `+180` in the
 * conditions table. Two copies of a convention is how a current row ends up
 * drawn backwards the day someone adds one: the table had no `toward` escape at
 * all, so the only bearing it could render correctly was a FROM bearing.
 *
 * @param dirDeg  the quoted bearing, degrees true
 * @param toward  true when the quantity is quoted TOWARD (currents, set)
 * @returns the compass bearing the glyph should POINT AT
 */
export function glyphHeading(dirDeg: number, toward = false): number {
  return toward ? dirDeg : dirDeg + 180;
}

/**
 * The arrow's geometry: pure numbers, no canvas and no colour, so the test can
 * rasterise exactly what ships instead of a copy of the construction.
 *
 * `heading` is the compass bearing the glyph POINTS AT — callers apply the
 * from/toward convention before calling. The glyph spans the full footprint,
 * tail at −`len` and tip at +`len` along that bearing.
 *
 * @returns `tail`, `tip`, and the head as `[apex, baseLeft, baseRight]` — fill
 *          order, and the same winding the map's SDF builds its head from.
 */
export function arrowGlyph(
  cx: number,
  cy: number,
  heading: number,
  len: number,
): {
  tail: [number, number];
  tip: [number, number];
  head: [[number, number], [number, number], [number, number]];
} {
  const a = (heading * Math.PI) / 180;
  const dx = Math.sin(a); // along the axis, toward the tip
  const dy = -Math.cos(a);
  const nx = -dy; // unit perpendicular
  const ny = dx;
  const tipX = cx + dx * len;
  const tipY = cy + dy * len;
  const h = ARROW_GLYPH.head;
  const halfW = h * Math.tan((ARROW_GLYPH.halfAngle * Math.PI) / 180);
  const baseX = tipX - dx * h;
  const baseY = tipY - dy * h;
  return {
    tail: [cx - dx * len, cy - dy * len],
    tip: [tipX, tipY],
    head: [
      [tipX, tipY],
      [baseX - nx * halfW, baseY - ny * halfW],
      [baseX + nx * halfW, baseY + ny * halfW],
    ],
  };
}

/**
 * Draw {@link arrowGlyph} on a 2D context: the shaft in the context's current
 * `strokeStyle`/`lineWidth`, the head filled in its `fillStyle`.
 *
 * The stroke weight stays the caller's, because it is a property of the surface
 * and not of the glyph — the chart ribbon draws at 1.4 and the conditions
 * table, which is denser, at 1.2. Everything about the SHAPE is here, so the
 * three surfaces that draw a bearing cannot drift into three dialects of it
 * again.
 */
export function drawArrowGlyph(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  heading: number,
  len: number,
): void {
  const { tail, tip, head } = arrowGlyph(cx, cy, heading, len);
  ctx.beginPath();
  ctx.moveTo(tail[0], tail[1]);
  ctx.lineTo(tip[0], tip[1]);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(head[0][0], head[0][1]);
  ctx.lineTo(head[1][0], head[1][1]);
  ctx.lineTo(head[2][0], head[2][1]);
  ctx.closePath();
  ctx.fill();
}

// A ribbon of small direction arrows along the top of the plot, one per hour
// (subsampled to avoid crowding). `toward` flips from the meteorological
// "coming from" convention to "flowing toward" (currents, swell set). One shape
// for every quantity — see {@link ARROW_GLYPH}; the panel names the variable.
export function dirArrows(dirs: Array<number | null>, opts?: { toward?: boolean }): Mark {
  const toward = opts?.toward === true;
  return {
    layer: "front",
    draw({ ctx, xPx, area }) {
      const n = dirs.length;
      if (n === 0) return;
      const px = area.right - area.left;
      const stride = Math.max(1, Math.round((n - 1) / Math.max(1, px / 40)));
      const y = area.top + 8;
      const color = cssVar("--ink-2", "#52514e");
      ctx.save();
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = 1.4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (let i = 0; i < n; i += stride) {
        const d = dirs[i];
        if (d === null || !Number.isFinite(d)) continue;
        drawArrowGlyph(ctx, xPx(i), y, glyphHeading(d, toward), 7);
      }
      ctx.restore();
    },
  };
}

/**
 * The seasonal normal: the middle half of hourly conditions for this month,
 * from ERA5 (forecast/primer.ts, ClimFact).
 *
 * A BAND, NOT A LINE. p25-p75 is a range, and drawing its midpoint as a single
 * rule would invent a "normal value" that the data does not contain — a reader
 * would then judge each hour above or below a number nobody computed. The band
 * says what is true: half of all hours in this month, historically, fall in
 * here.
 *
 * Layer "back", and very quiet. This is context for the forecast, never a
 * threshold to act on — the limit line is the only horizontal rule on these
 * charts that means anything operational, and the normal must not compete with
 * it. It is drawn beneath the series and the limit for exactly that reason.
 *
 * The caller must pass values in the CHART's y units. The band is climatology
 * in metres or knots and the chart may be plotting feet, so the conversion
 * belongs at the call site with every other display conversion.
 */
export function normalBand(
  lo: number,
  hi: number,
  opts?: { label?: string },
): Mark {
  return {
    layer: "back",
    draw({ ctx, yPx, area }) {
      if (!(Number.isFinite(lo) && Number.isFinite(hi)) || hi <= lo) return;
      // Clamp to the plot: a normal that runs off the top of a calm-window axis
      // should still show its lower edge rather than vanish entirely.
      const yTop = Math.max(area.top, Math.min(yPx(hi), area.bottom));
      const yBot = Math.min(area.bottom, Math.max(yPx(lo), area.top));
      if (yBot <= yTop) return;
      const ink = cssVar("--ink-2", "#48575f");
      ctx.save();
      ctx.fillStyle = rgba(ink, 0.07);
      ctx.fillRect(area.left, yTop, area.right - area.left, yBot - yTop);
      ctx.strokeStyle = rgba(ink, 0.28);
      ctx.setLineDash([3, 4]);
      ctx.lineWidth = 1;
      for (const y of [yTop, yBot]) {
        // Only draw an edge that is really the band's, not the clamp's.
        if (y <= area.top + 0.5 || y >= area.bottom - 0.5) continue;
        ctx.beginPath();
        ctx.moveTo(area.left, Math.round(y) + 0.5);
        ctx.lineTo(area.right, Math.round(y) + 0.5);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      const txt = opts?.label ?? "seasonal normal";
      if (yBot - yTop >= 13) {
        ctx.font = "500 9.5px system-ui, sans-serif";
        ctx.textBaseline = "alphabetic";
        ctx.textAlign = "left";
        ctx.fillStyle = rgba(ink, 0.62);
        ctx.fillText(txt, area.left + 5, Math.min(yBot - 3, yTop + 11));
      }
      ctx.restore();
    },
  };
}
